import React, { useState, useRef, useEffect } from 'react';
import { Star, X, ArrowRight, Plus, MessageSquare, FileText, Trash2 } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useKeyFindings } from '../contexts/KeyFindingsContext';
import { useAskSidebar } from '../contexts/AskSidebarContext';
import { formatDate } from '../utils/dateFormat';
import { parsePiiMarkers } from '../utils/parsePiiMarkers';

const EVENT_TYPE_LABELS: Record<string, string> = {
  HOSPITAL_VISIT: 'Hospital Visit',
  OUTPATIENT_APPOINTMENT: 'Outpatient',
  EMERGENCY_VISIT: 'Emergency',
  SURGICAL_EPISODE: 'Surgery',
  TREATMENT_COURSE: 'Treatment',
  ADMISSION: 'Admission',
  DISCHARGE: 'Discharge',
  DIAGNOSIS: 'Diagnosis',
  MAJOR_PROCEDURE: 'Procedure',
  MEDICATION_ROUTINE: 'Medication',
  VITAL_SIGNS: 'Vitals',
  LAB_RESULT: 'Lab Result',
  CONSULTATION: 'Consultation',
};

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const KeyFindingsIndicator: React.FC = () => {
  const { findings, findingsCount, addFinding, removeFinding, clearAllFindings } = useKeyFindings();
  const { addToContext, open: openAsk, setActiveSidebarTab } = useAskSidebar();
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newFindingText, setNewFindingText] = useState('');
  const [confirmingClear, setConfirmingClear] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Close dropdown on click-away or Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsAdding(false);
        setNewFindingText('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isAdding) {
          setIsAdding(false);
          setNewFindingText('');
        } else {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isAdding]);

  // Focus input when entering add mode
  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const scrollToEvent = (eventId: string, retries = 5) => {
    const el = document.querySelector(`[data-event-id="${CSS.escape(eventId)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash the element itself (it carries the border)
      const target = el as HTMLElement;
      target.style.animation = 'border-pulse-amber 0.6s ease-in-out 3';
      target.addEventListener('animationend', () => { target.style.animation = ''; }, { once: true });
    } else if (retries > 0) {
      // Tab may still be rendering — retry
      setTimeout(() => scrollToEvent(eventId, retries - 1), 200);
    }
  };

  /** Map an eventId to the tab it lives on */
  const OVERVIEW_SECTION_IDS = ['caseSummary', 'index_event', 'treatment', 'current_status', 'prognosis'];
  const getTabForEvent = (eventId: string): string => {
    if (eventId.includes('-op-')) return 'expert-evidence';
    if (eventId.startsWith('gap-') || eventId.startsWith('inc-')) return 'evidence-integrity';
    if (eventId.startsWith('caus-')) return 'causation';
    if (eventId.startsWith('soc-') || eventId.startsWith('comp-')) return 'clinical-standards';
    if (eventId.startsWith('section-')) {
      const sectionKey = eventId.replace('section-', '');
      return OVERVIEW_SECTION_IDS.includes(sectionKey) ? 'overview' : 'claimant-background';
    }
    return 'chronology';
  };

  const handleJumpToEvent = (eventId: string | null) => {
    if (!eventId || !caseId) return;
    setIsOpen(false);

    const targetTab = getTabForEvent(eventId);
    const currentPath = location.pathname;
    const targetPath = `/cases/${caseId}/${targetTab}?highlight=${encodeURIComponent(eventId)}`;

    if (currentPath.includes(`/${targetTab}`)) {
      // Already on this tab — set the param so the panel can react
      navigate(targetPath, { replace: true });
      // Also try direct scroll for chronology (elements already in DOM)
      setTimeout(() => scrollToEvent(eventId), 200);
    } else {
      navigate(targetPath);
    }
  };

  const handleSubmitNewFinding = () => {
    const text = newFindingText.trim();
    if (!text) return;
    addFinding({ selectedText: text });
    setNewFindingText('');
    setIsAdding(false);
  };

  const handleAskAboutFindings = () => {
    findings.forEach((finding) => {
      const summary = finding.selectedText
        || finding.eventHeadline
        || 'Marked event';
      addToContext({
        id: finding.id,
        type: 'event' as any,
        summary,
        source: `Key Finding — ${finding.eventType ? (EVENT_TYPE_LABELS[finding.eventType] || finding.eventType) : 'Note'}`,
        data: { eventId: finding.eventId, type: finding.eventType, timestamp: finding.eventDate },
      });
    });
    openAsk();
    setIsOpen(false);
  };

  const handleDraftReport = () => {
    openAsk();
    setActiveSidebarTab('wizard');
    setIsOpen(false);
  };

  const hasFindings = findingsCount > 0;

  return (
    <div className="relative" ref={panelRef}>
      {/* Pill button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
          hasFindings
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
            : 'bg-gray-100 dark:bg-medical-800 text-gray-500 dark:text-medical-400'
        }`}
        title={hasFindings ? `${findingsCount} key finding${findingsCount !== 1 ? 's' : ''}` : 'No key findings marked'}
      >
        <Star className="h-3.5 w-3.5" fill={hasFindings ? 'currentColor' : 'none'} />
        <span>{findingsCount} Finding{findingsCount !== 1 ? 's' : ''}</span>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[75vh] bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-700 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-5 py-3.5 border-b border-medical-100 dark:border-medical-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-medical-900 dark:text-medical-100 flex items-center gap-2">
                <div className="p-1 rounded-md bg-amber-100 dark:bg-amber-900/40">
                  <Star className="h-4 w-4 text-amber-500" fill="currentColor" />
                </div>
                Key Findings
                {findingsCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white">
                    {findingsCount}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsAdding(!isAdding)}
                  className={`p-1.5 rounded-md transition-colors ${
                    isAdding
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                      : 'hover:bg-medical-100 dark:hover:bg-medical-700 text-medical-400 hover:text-medical-600'
                  }`}
                  title="Add a custom finding"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  onClick={() => { setIsOpen(false); setIsAdding(false); setNewFindingText(''); setConfirmingClear(false); }}
                  className="p-1.5 hover:bg-medical-100 dark:hover:bg-medical-700 rounded-md transition-colors"
                >
                  <X className="h-4 w-4 text-medical-400" />
                </button>
              </div>
            </div>
            {hasFindings && (
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-[11px] text-medical-400 dark:text-medical-500">
                  Star events or select text to build your findings
                </p>
                {!confirmingClear ? (
                  <button
                    onClick={() => {
                      if (findingsCount > 2) {
                        setConfirmingClear(true);
                      } else {
                        clearAllFindings();
                      }
                    }}
                    className="text-[11px] text-medical-400 dark:text-medical-500 hover:text-red-500 dark:hover:text-red-400 transition-colors flex-shrink-0 ml-2"
                  >
                    Clear all
                  </button>
                ) : (
                  <span className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <span className="text-[11px] text-red-500 dark:text-red-400">Remove {findingsCount}?</span>
                    <button
                      onClick={() => { clearAllFindings(); setConfirmingClear(false); }}
                      className="text-[11px] font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setConfirmingClear(false)}
                      className="text-[11px] font-medium text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300 transition-colors"
                    >
                      No
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* New finding form */}
          {isAdding && (
            <div className="px-5 py-3 border-b border-medical-100 dark:border-medical-700 bg-amber-50/50 dark:bg-amber-900/10">
              <textarea
                ref={inputRef}
                value={newFindingText}
                onChange={(e) => setNewFindingText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmitNewFinding();
                  }
                }}
                placeholder="Type a key finding..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-medical-200 dark:border-medical-600 rounded-lg bg-white dark:bg-medical-800 text-medical-900 dark:text-medical-100 placeholder-medical-400 dark:placeholder-medical-500 focus:outline-none focus:ring-2 focus:ring-amber-400 dark:focus:ring-amber-500 focus:border-transparent resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-medical-400 dark:text-medical-500">
                  {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}+Enter to save
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setIsAdding(false); setNewFindingText(''); }}
                    className="px-2.5 py-1 text-xs text-medical-500 hover:text-medical-700 dark:text-medical-400 dark:hover:text-medical-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitNewFinding}
                    disabled={!newFindingText.trim()}
                    className="px-3 py-1 text-xs font-medium rounded-md bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Findings list */}
          <div className="flex-1 overflow-y-auto">
            {findings.length === 0 && !isAdding ? (
              <div className="px-5 py-10 text-center">
                <div className="inline-flex p-3 rounded-full bg-amber-50 dark:bg-amber-900/20 mb-3">
                  <Star className="h-6 w-6 text-amber-300 dark:text-amber-600" />
                </div>
                <p className="text-sm font-medium text-medical-600 dark:text-medical-300 mb-1">
                  No key findings yet
                </p>
                <p className="text-xs text-medical-400 dark:text-medical-500 mb-4">
                  Star chronology events, select text, or add your own notes
                </p>
                <button
                  onClick={() => setIsAdding(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add a finding
                </button>
              </div>
            ) : (
              <div>
                {findings.map((finding, index) => {
                  const isFreeform = !finding.eventId;
                  const eid = finding.eventId || '';
                  // Detect synthetic (non-chronology) IDs by known prefixes
                  const isSyntheticId = eid.includes('-op-') || eid.startsWith('gap-') || eid.startsWith('inc-') || eid.startsWith('caus-') || eid.startsWith('section-') || eid.startsWith('soc-') || eid.startsWith('comp-');
                  const isNonChronology = !isFreeform && isSyntheticId;
                  const isChronologyEvent = !isFreeform && !isSyntheticId;
                  const isTextSelection = !isFreeform && finding.selectedText && finding.startOffset != null;

                  // Two-level label: category (type badge) + sub-type
                  const getLabel = (): { category: string; categoryClass: string; subType?: string } => {
                    if (isFreeform) return { category: 'Note', categoryClass: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' };
                    if (isChronologyEvent) {
                      const sub = finding.eventType
                        ? (EVENT_TYPE_LABELS[finding.eventType] || finding.eventType.replace(/_/g, ' '))
                        : undefined;
                      return { category: 'Chronology', categoryClass: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400', subType: sub };
                    }
                    // Expert evidence: {docUUID}-op-{idx}
                    if (eid.includes('-op-')) return { category: 'Expert Evidence', categoryClass: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400', subType: 'Opinion' };
                    // Gap analysis: gap-{catKey}-{idx}
                    if (eid.startsWith('gap-')) {
                      const catKey = eid.replace(/^gap-/, '').replace(/-\d+$/, '').replace(/_/g, ' ');
                      return { category: 'Gap Analysis', categoryClass: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400', subType: capitalise(catKey) };
                    }
                    // Inconsistency: inc-{catKey}-{idx}
                    if (eid.startsWith('inc-')) {
                      const catKey = eid.replace(/^inc-/, '').replace(/-\d+$/, '').replace(/_/g, ' ');
                      return { category: 'Inconsistency', categoryClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400', subType: capitalise(catKey) };
                    }
                    // Causation: caus-{catKey}-{idx}
                    if (eid.startsWith('caus-')) {
                      const catKey = eid.replace(/^caus-/, '').replace(/-\d+$/, '').replace(/_/g, ' ');
                      return { category: 'Causation', categoryClass: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', subType: capitalise(catKey) };
                    }
                    // Clinical standards: soc-{type}-{idx} or comp-{type}-{idx}
                    if (eid.startsWith('soc-') || eid.startsWith('comp-')) {
                      const subKey = eid.replace(/^(soc|comp)-/, '').replace(/-\d+$/, '').replace(/_/g, ' ');
                      return { category: 'Clinical Standards', categoryClass: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400', subType: capitalise(subKey) };
                    }
                    // Narrative section: section-{sectionKey}
                    if (eid.startsWith('section-')) {
                      const sectionName = eid.replace('section-', '').replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                      return { category: 'Overview', categoryClass: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400', subType: capitalise(sectionName) };
                    }
                    return { category: 'Finding', categoryClass: 'bg-medical-100 dark:bg-medical-700 text-medical-500 dark:text-medical-400' };
                  };
                  // Strip PII tags and citation markers from display text, truncate to max length
                  const cleanText = (text: string | null, maxLen = 120) => {
                    if (!text) return null;
                    const clean = parsePiiMarkers(text).cleanText.replace(/\s*\[\d+\]/g, '');
                    if (clean.length <= maxLen) return clean;
                    return clean.slice(0, maxLen).trimEnd() + '\u2026';
                  };
                  const displayText = cleanText(finding.selectedText);
                  const displayHeadline = cleanText(finding.eventHeadline);
                  const isJumpable = !isFreeform;
                  return (
                    <div
                      key={finding.id}
                      onClick={isJumpable ? () => handleJumpToEvent(finding.eventId) : undefined}
                      className={`px-4 py-2.5 hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors group ${
                        isJumpable ? 'cursor-pointer' : ''
                      } ${
                        index !== findings.length - 1 ? 'border-b border-medical-100 dark:border-medical-700/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        {/* Number badge */}
                        <div className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center text-[9px] font-bold mt-0.5">
                          {index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Two-level label: category + sub-type */}
                          {(() => { const { category, categoryClass, subType } = getLabel(); return (
                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                              <span className={`inline-flex items-center px-1 py-px rounded text-[9px] font-medium uppercase tracking-wide ${categoryClass}`}>
                                {category}
                              </span>
                              {subType && (
                                <span className="text-[10px] font-medium text-medical-500 dark:text-medical-400">
                                  {subType}
                                </span>
                              )}
                              {isChronologyEvent && finding.eventDate && (
                                <span className="text-[11px] font-medium text-medical-500 dark:text-medical-400 tabular-nums">
                                  {formatDate(finding.eventDate)}
                                </span>
                              )}
                            </div>
                          ); })()}

                          {/* Content */}
                          {displayText ? (
                            <p className="text-xs text-medical-700 dark:text-medical-300 leading-snug">
                              {isFreeform || (isNonChronology && !isTextSelection) ? (
                                displayText
                              ) : isTextSelection ? (
                                <>
                                  <span className="text-medical-400 dark:text-medical-500">&ldquo;</span>
                                  {displayText}
                                  <span className="text-medical-400 dark:text-medical-500">&rdquo;</span>
                                </>
                              ) : (
                                <span className="font-medium">{displayText}</span>
                              )}
                            </p>
                          ) : displayHeadline ? (
                            <p className="text-xs text-medical-700 dark:text-medical-300 leading-snug font-medium">
                              {displayHeadline}
                            </p>
                          ) : (
                            <p className="text-xs text-medical-500 dark:text-medical-400">
                              Entire event marked
                            </p>
                          )}

                          {/* Note */}
                          {finding.note && (
                            <p className="text-[11px] text-medical-400 dark:text-medical-500 mt-0.5">
                              {finding.note}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isJumpable && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleJumpToEvent(finding.eventId); }}
                              className="p-1.5 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-md text-medical-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                              title="Jump to finding"
                            >
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => removeFinding(finding.id)}
                            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md text-medical-300 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            title="Remove finding"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};
